/* eslint-disable import/no-duplicates */
import { useEffect, useState } from "react";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { Stack, TextFieldProps } from "@mui/material";
import {
  isFuture,
  isValid,
  parseISO,
  startOfDay,
  startOfYear,
  subDays,
  subMonths,
  subYears,
} from "date-fns";
import { enGB } from "date-fns/locale";
import type { SelectOption } from "../fieldTypes";
import { InputSelectValue, Select } from "./Select";

const DatePickerIcon = () => (
  <i className="udo-icon-datepicker" style={{ width: 24, height: 24 }} />
);

const DATE_MODE = {
  YTD: "ytd",
  LAST_30_DAYS: "last_30_days",
  LAST_90_DAYS: "last_90_days",
  LAST_1_MONTHS: "last_month",
  LAST_6_MONTHS: "last_6_months",
  LAST_YEAR: "last_year",
  LAST_2_YEARS: "last_2_years",
  LAST_3_YEARS: "last_3_years",
  INDIVIDUAL: "individual",
} as const;

export type DateMode = (typeof DATE_MODE)[keyof typeof DATE_MODE];
type DateRangeBoundary = "from" | "to";

interface DateRangeValue {
  date_from: Date;
  date_to: Date;
}

interface DateRangeState {
  from: Date | null;
  to: Date | null;
}

interface DateRangeExtra {
  individual_date_from?: string | number | Date;
}

interface DateRangeProps {
  onDateChange: (value: DateRangeValue) => void;
  value: DateMode;
  label?: string;
  size?: TextFieldProps["size"];
  options: SelectOption[];
  setDateMode: (value: DateMode) => void;
  extra?: DateRangeExtra;
}

const isDateMode = (value: string): value is DateMode =>
  Object.values(DATE_MODE).some((mode) => mode === value);

export const DateRange = ({
  onDateChange,
  value,
  label = "",
  size,
  options,
  setDateMode,
  extra,
}: DateRangeProps) => {
  const [dateRangeValues, setDateRangeValues] = useState<DateRangeState>({
    from: null,
    to: null,
  });
  const [fromOpened, setFromOpened] = useState(false);
  const [toOpened, setToOpened] = useState(false);
  const [mode, setMode] = useState(value);
  const [validMsg, setValidMsg] = useState<string | null>(null);
  const currentLangLocale = enGB;

  const checkValid = (
    dateRange: DateRangeState
  ): dateRange is {
    from: Date;
    to: Date;
  } => {
    const { from, to } = dateRange;

    if (!from || !to) {
      return false;
    }

    if (!!from && !!to && (!isValid(from) || !isValid(to))) {
      setValidMsg("Incorrect data type");
      return false;
    }

    if (from > to) {
      setValidMsg("Date to should be greater or equal than date from");
      return false;
    }

    setValidMsg(null);
    return true;
  };

  const handleChange = (value: Date | null, type: DateRangeBoundary) => {
    let newDateRange = dateRangeValues;

    if (type === "from") {
      newDateRange = { ...newDateRange, from: value };
    }

    if (type === "to") {
      newDateRange = { ...newDateRange, to: value };
    }

    setDateRangeValues(newDateRange);
    if (checkValid(newDateRange)) {
      onDateChange({ date_from: newDateRange.from, date_to: newDateRange.to });
    }
  };

  const handleModeChange = (value: InputSelectValue) => {
    if (typeof value === "string" && isDateMode(value)) {
      setMode(value);
      setDateMode(value);
    }
  };

  const handleIndividual = (newValue: [Date, Date]) => {
    const [dateFrom, dateTo] = newValue;
    setDateRangeValues({ from: dateFrom, to: dateTo });
    onDateChange({ date_from: dateFrom, date_to: dateTo });
  };

  useEffect(() => {
    const dateTo = startOfDay(new Date());
    let dateFrom = dateTo;
    switch (mode) {
      case DATE_MODE.YTD:
        const startMonth = 1;
        const dateYtd = startOfDay(
          new Date(new Date().getFullYear(), startMonth, 1)
        );
        if (isFuture(dateYtd)) {
          dateFrom = startOfYear(subYears(new Date(), 1));
        } else {
          dateFrom = dateYtd;
        }
        break;
      case DATE_MODE.LAST_30_DAYS:
        dateFrom = startOfDay(subDays(new Date(), 30));
        break;
      case DATE_MODE.LAST_90_DAYS:
        dateFrom = startOfDay(subDays(new Date(), 90));
        break;
      case DATE_MODE.LAST_1_MONTHS:
        dateFrom = startOfDay(subMonths(new Date(), 1));
        break;
      case DATE_MODE.LAST_6_MONTHS:
        dateFrom = startOfDay(subMonths(new Date(), 6));
        break;
      case DATE_MODE.LAST_YEAR:
        dateFrom = startOfDay(subYears(new Date(), 1));
        break;
      case DATE_MODE.LAST_2_YEARS:
        dateFrom = startOfDay(subYears(new Date(), 2));
        break;
      case DATE_MODE.LAST_3_YEARS:
        dateFrom = startOfDay(subYears(new Date(), 3));
        break;
      case DATE_MODE.INDIVIDUAL:
        dateFrom = extra?.individual_date_from
          ? startOfDay(new Date(extra.individual_date_from))
          : startOfDay(subYears(new Date(), 1));
        break;
      default:
        break;
    }
    handleIndividual([dateFrom, dateTo]);
  }, [mode]);

  const datePickerOptions = {
    disableToolbar: true,
    inputFormat: "dd/MM/yyyy",
    KeyboardButtonProps: {
      "aria-label": "change date",
    },
    keyboardIcon: <DatePickerIcon />,
    cancelLabel: "cancel",
    clearLabel: "clear",
    okLabel: "ok",
  };

  return (
    <LocalizationProvider
      dateAdapter={AdapterDateFns}
      adapterLocale={currentLangLocale}
    >
      <Stack direction="row" spacing={1}>
        <Select
          value={mode}
          onChange={handleModeChange}
          label={label}
          options={options}
          errorMsg={validMsg ?? ""}
          error={!!validMsg}
          sx={{ minWidth: 200 }}
        />
        {mode === DATE_MODE.INDIVIDUAL && (
          <>
            <DatePicker
              {...datePickerOptions}
              label="from"
              value={dateRangeValues.from || parseISO("")}
              onChange={(value) => {
                setFromOpened(false);
                handleChange(value, "from");
              }}
              open={fromOpened}
              onOpen={() => setFromOpened(true)}
              onClose={() => setFromOpened(false)}
              slotProps={{
                textField: { size },
              }}
              sx={{ minWidth: 200 }}
            />
            <DatePicker
              {...datePickerOptions}
              label="to"
              value={dateRangeValues.to || parseISO("")}
              onChange={(value) => {
                setToOpened(false);
                handleChange(value, "to");
              }}
              open={toOpened}
              onOpen={() => setToOpened(true)}
              onClose={() => setToOpened(false)}
              slotProps={{
                textField: { size },
              }}
              sx={{ minWidth: 200 }}
            />
          </>
        )}
      </Stack>
    </LocalizationProvider>
  );
};
